import {
  AddMeetingSuggestionDocument,
  GetMeetingEntryDocument,
  GetMeetingRangeDocument,
  MeetingCancelSignupScreenDocument,
  MeetingSignupScreenDocument,
  MeetingSubscription,
  ScreenProfile,
} from '@/graphql/generated-types';
import type { Filter } from '@/graphql/generated-schema-types';
import { apolloClient } from '@/utils/apollo';
import SentryGraphQLError from '@/utils/exceptions/SentryGraphQLError';

const addSuggestion = async ({
  profile,
  meetingId,
  suggestion,
}: {
  profile: ScreenProfile;
  meetingId: string;
  suggestion: string;
}) => {
  const response = await apolloClient.mutate({
    mutation: AddMeetingSuggestionDocument,
    variables: {
      profile,
      meetingId,
      suggestion,
    },
    errorPolicy: 'all',
  });

  if (response.data?.meeting?.suggest?.success) {
    return response.data;
  }

  throw new SentryGraphQLError(`api.meetings.addSuggestion`, response);
};

const signup = async (
  subscription: MeetingSubscription,
  profile: ScreenProfile
) => {
  const response = await apolloClient.mutate({
    mutation: MeetingSignupScreenDocument,
    variables: {
      subscription,
      profile,
    },
    errorPolicy: 'all',
  });

  if (response.data?.meeting?.signup?.success) {
    return response.data.meeting.signup;
  }

  throw new SentryGraphQLError(`api.meetings.signup`, response);
};

const cancelSignup = async (
  subscription: MeetingSubscription,
  profile: ScreenProfile
) => {
  const response = await apolloClient.mutate({
    mutation: MeetingCancelSignupScreenDocument,
    variables: {
      subscription,
      profile,
    },
    errorPolicy: 'all',
  });

  if (response.data?.meeting?.cancelSignup?.success) {
    return response.data.meeting.cancelSignup;
  }

  throw new SentryGraphQLError(`api.meetings.cancelSignup`, response);
};

const getMeetingRange = async (
  filter: Filter,
  dateFrom: string,
  dateTo: string,
  timezone: string
) => {
  const response = await apolloClient.query({
    query: GetMeetingRangeDocument,
    variables: {
      filter,
      dateFrom,
      dateTo,
      timezone,
    },
    errorPolicy: 'all',
    fetchPolicy: 'no-cache',
  });

  if (response.data?.meetingRange) {
    return response.data.meetingRange;
  }

  throw new SentryGraphQLError(`api.meetings.getMeetingRange`, response);
};

const getMeetingEntry = async (meetingId: string) => {
  const response = await apolloClient.query({
    query: GetMeetingEntryDocument,
    variables: {
      id: meetingId,
    },
    errorPolicy: 'all',
    fetchPolicy: 'no-cache',
  });

  if (response.data?.meetingEntry) {
    return response.data.meetingEntry;
  }

  throw new SentryGraphQLError(`api.meetings.getMeetingEntry`, response);
};

export default {
  addSuggestion,
  signup,
  cancelSignup,
  getMeetingRange,
  getMeetingEntry,
};
